"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, BookOpen, CircleAlert as AlertTriangle, CircleCheck as CheckCircle, ArrowRight, Settings } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Maintenance() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="maintenance-schedule" data-nav="light" className="bg-background text-foreground border-t border-border py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 border-b border-border pb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Documentation & Servicing Guidelines
              </span>
              <h2 className="font-serif text-3xl font-bold uppercase tracking-tight sm:text-4xl text-foreground">
                Follow the Maintenance Schedule for Your Machine
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="border-border font-mono text-xs uppercase tracking-wider h-10 px-4 transition-[border-color,background-color] hover:border-foreground" asChild>
                <Link to="/ContactPage">
                  <FileText className="size-4 mr-2" />
                  Contact Documentation Support
                </Link>
              </Button>
            </div>
          </div>
          <p className="mt-4 max-w-3xl font-sans text-base text-muted-foreground leading-relaxed">
            Mechanical bulls contain moving, electrical and supporting components that may require maintenance. However, maintenance schedules and procedures can differ between models.
          </p>
        </div>

        {/* 2-Column Industrial Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left Column: Machine Documentation & Inspection Requirements */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          ease: "easeOut"
        }} className="lg:col-span-6 flex flex-col gap-6">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
                <div className="flex items-center gap-2">
                  <BookOpen className="size-5 text-primary" />
                  <h3 className="font-serif text-xl font-semibold uppercase tracking-wide text-foreground">
                    Model-Specific Documentation
                  </h3>
                </div>
                <span className="font-mono text-xs uppercase bg-muted px-2.5 py-1 text-foreground border border-border">
                  Official Guidance
                </span>
              </div>

              <div className="space-y-4">
                <div className="border border-border bg-background p-4 transition-[border-color] duration-150 hover:border-foreground">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 p-1.5 bg-muted border border-border">
                      <Settings className="size-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-base font-semibold uppercase tracking-wide text-foreground mb-1">
                        Equipment Assemblies
                      </h4>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        Mechanical bulls contain moving, electrical and supporting components that may require maintenance. However, maintenance schedules and procedures can differ between models.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-border bg-background p-4 transition-[border-color] duration-150 hover:border-foreground">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 p-1.5 bg-muted border border-border">
                      <FileText className="size-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-base font-semibold uppercase tracking-wide text-foreground mb-1">
                        Manual & Product Documentation
                      </h4>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        Use the manual or product documentation for the exact machine to determine what requires inspection, adjustment, lubrication or replacement and when those tasks should occur.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-border bg-background p-4 transition-[border-color] duration-150 hover:border-foreground">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 p-1.5 bg-muted border border-border">
                      <AlertTriangle className="size-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-base font-semibold uppercase tracking-wide text-foreground mb-1">
                        Component Replacement Rule
                      </h4>
                      <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                        If a component needs replacement, confirm compatibility before installing another part.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 border-t border-border pt-4">
                <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
                  <div className="size-2 bg-primary shrink-0" />
                  <span>Always refer to the manufacturer manual specific to your serial and model number.</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Servicing Visual + Replacement Parts & Compatibility */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-6 flex flex-col gap-6">
            {/* Operational Image Card */}
            <div className="border border-border bg-card overflow-hidden">
              <div className="relative aspect-16/9 bg-muted border-b border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/technician_servicing_motor_base.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/technician_servicing_motor_base.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/technician_servicing_motor_base.png-wvc-sizes" alt="Industrial equipment technician servicing a mechanical bull dual-axis motor base and gearbox" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale contrast-125" />
                <div className="absolute top-3 left-3 bg-secondary text-secondary-foreground font-mono text-xs uppercase px-2.5 py-1 tracking-wider border border-border">
                  Mechanical Bull Servicing
                </div>
              </div>

              {/* Component Replacement Card Block */}
              <div className="p-6">
                <div className="flex items-center justify-between border-b border-border pb-4 mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="size-5 text-primary" />
                    <h3 className="font-serif text-xl font-semibold uppercase tracking-wide text-foreground">
                      Parts & Component Verification
                    </h3>
                  </div>
                  <span className="font-mono text-xs uppercase text-muted-foreground">
                    Compatibility Check
                  </span>
                </div>

                <div className="space-y-3 font-sans text-sm text-muted-foreground leading-relaxed mb-6">
                  <p>
                    If a component needs replacement, confirm compatibility before installing another part.
                  </p>
                  <p>
                    Use the manual or product documentation for the exact machine to determine what requires inspection, adjustment, lubrication or replacement and when those tasks should occur.
                  </p>
                </div>

                <div className="border-t border-border pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="text-xs font-mono text-muted-foreground">
                    Confirm OEM model specs prior to ordering replacement parts.
                  </div>
                  <Button variant="default" className="font-serif uppercase tracking-wider font-semibold text-sm h-10 px-6 bg-primary text-primary-foreground transition-colors hover:bg-primary/90 shrink-0" asChild>
                    <Link to="/MechanicalBullPartsAndAccessoriesPage">
                      Browse Parts & Accessories
                      <ArrowRight className="size-4 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Industrial Spec Plate Strip */}
        <div className="mt-12 border border-border bg-muted p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-background border border-border">
              <FileText className="size-5 text-primary" />
            </div>
            <div>
              <span className="font-serif uppercase font-semibold text-base tracking-wide text-foreground block">
                Technical Manuals & Parts Guidance
              </span>
              <span className="font-sans text-xs text-muted-foreground">
                Need help verifying machine compatibility? Contact our technical team with your model details.
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 font-mono text-xs">
            <Button variant="outline" size="sm" className="border-border bg-background font-mono uppercase text-xs hover:border-foreground" asChild>
              <Link to="/ContactPage">Contact Support</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}